"use client";

import { toast } from "./Toaster";
import { useWishlist } from "@/lib/auth/use-wishlist";
import { useTranslations } from "next-intl";

interface Props {
  /** Listing wp_post_id (or numeric id). String accepted for legacy callers
   *  that pass a stringified number — coerced on entry. */
  id: string | number;
  /** Optional slug — recorded with the wishlist row for debugging / display. */
  slug?: string | null;
  /**
   * `"card"` — default. Absolute-positioned heart over a photo card.
   * `"inline"` — 40×40 circular button that sits in normal flow, used next
   *   to the Share button on listing detail pages.
   */
  variant?: "card" | "inline";
}

export default function WishlistHeart({ id, slug, variant = "card" }: Props) {
  const t = useTranslations("wishlistHeart");
  const numericId = typeof id === "number" ? id : parseInt(String(id), 10);
  const { isMember, toggle, isReady } = useWishlist();
  const on = Number.isFinite(numericId) && isMember(numericId);

  async function handle(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (!Number.isFinite(numericId)) return;
    const wasOn = on;
    await toggle(numericId, slug ?? null);
    toast(wasOn ? t("removedToast") : t("savedToast"), wasOn ? undefined : "success");
  }

  const isInline = variant === "inline";

  return (
    <button
      type="button"
      onClick={handle}
      aria-pressed={on}
      aria-label={on ? t("removeFromWishlist") : t("saveToWishlist")}
      title={on ? t("removeFromWishlist") : t("saveToWishlist")}
      // Hide via opacity (not display:none) while loading so layout is stable
      // and the heart "appears" with the correct state once the hook is ready.
      style={isReady ? undefined : { opacity: 0.5 }}
      className={
        isInline
          ? "w-10 h-10 rounded-full bg-white border border-ss-line grid place-items-center hover:border-ss-text transition-colors cursor-pointer"
          : "absolute top-2.5 end-2.5 w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow-[0_1px_2px_rgba(0,0,0,0.15)] hover:scale-110 transition-transform z-10 cursor-pointer"
      }
    >
      <svg
        width={isInline ? 18 : 18}
        height={isInline ? 18 : 18}
        viewBox="0 0 24 24"
        fill={on ? "#e0245e" : "none"}
        stroke={on ? "#e0245e" : "#0b1233"}
        strokeWidth="2"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z"
        />
      </svg>
    </button>
  );
}
